// Rate-limited TMDB request queue service
// Handles concurrent requests, deduplication, and automatic retry with backoff
// Now also checks Firestore cache before making API calls

import { formatMovieDisplayTitleFromId, movieCacheService } from '@watchlist/shared';
import { shouldUseFunctionsAPI } from './tmdbFunctionsApi';

/** Where a single-movie payload came from (for performance telemetry). */
export type TmdbMovieDataSource = 'memory' | 'firestore' | 'network';

/**
 * `createFallbackMovie` uses id 0 and Response "True" + title — never persist those or we poison
 * localStorage (especially on Android WebView) for the full TTL with no poster / real metadata.
 */
function tmdbNumericIdFromPayload(d: any): number {
  if (d?.id == null) return 0;
  const n = typeof d.id === 'number' ? d.id : parseInt(String(d.id), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isRealTmdbPayloadForQueueCache(d: any): boolean {
  if (!d || typeof d !== 'object') return false;
  if (d.Response === 'False') return false;
  return tmdbNumericIdFromPayload(d) > 0;
}

interface QueuedRequest {
  id: string;
  movieId: string;
  resolve: (value: { data: any; source: 'network' }) => void;
  reject: (error: any) => void;
  timestamp: number;
  retryCount: number;
}

interface CachedMovie {
  data: any;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class TMDbRequestQueue {
  private queue: QueuedRequest[] = [];
  private pendingRequests: Map<string, Promise<{ data: any; source: TmdbMovieDataSource }>> = new Map();
  private cache: Map<string, CachedMovie> = new Map();
  private isProcessing = false;
  private readonly MAX_CONCURRENT = 5; // 5 concurrent requests per batch
  private readonly RATE_LIMIT_DELAY = 500; // 500ms between batches
  private readonly CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours cache - movie data rarely changes
  private readonly STORAGE_KEY = 'tmdb_movie_cache';
  private readonly CACHE_VERSION_KEY = 'tmdb_movie_cache_version';
  // Increment when cache format or eligibility rules change (v3: do not store fallback id:0 payloads)
  private readonly CACHE_VERSION = 3;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAYS = [1000, 2000, 5000]; // Exponential backoff
  private activeRequests = 0;
  private lastRequestTime = 0;

  constructor() {
    this.checkCacheVersion();
    this.loadCacheFromStorage();
    // Save cache to localStorage every 30 seconds
    setInterval(() => this.saveCacheToStorage(), 30000);
  }

  /**
   * Check if cache version matches, clear if outdated
   */
  private checkCacheVersion(): void {
    try {
      const storedVersion = localStorage.getItem(this.CACHE_VERSION_KEY);
      const currentVersion = this.CACHE_VERSION.toString();

      if (storedVersion !== currentVersion) {
        console.log(`🔄 Cache version mismatch (${storedVersion} -> ${currentVersion}), clearing stale cache...`);
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.setItem(this.CACHE_VERSION_KEY, currentVersion);
      }
    } catch (error) {
      console.warn('Failed to check cache version:', error);
    }
  }

  /**
   * Add a movie request to the queue with deduplication
   * Now checks: 1) Local cache, 2) Firestore cache, 3) TMDB API
   */
  async getMovieData(movieId: string): Promise<any> {
    const { data } = await this.getMovieDataWithSource(movieId);
    return data;
  }

  /**
   * Same as getMovieData but includes whether the payload came from memory, Firestore movie cache, or TMDB.
   */
  async getMovieDataWithSource(movieId: string): Promise<{ data: any; source: TmdbMovieDataSource }> {
    const cacheKey = this.getCacheKey(movieId);

    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log(`📦 Local cache hit for movie ${movieId}`);
      return { data: cached, source: 'memory' };
    }

    if (this.pendingRequests.has(cacheKey)) {
      console.log(`⏳ Deduplicating request for movie ${movieId}`);
      return this.pendingRequests.get(cacheKey)!;
    }

    const firestorePromise = this.checkFirestoreCacheWithSource(movieId, cacheKey);

    this.pendingRequests.set(cacheKey, firestorePromise);

    firestorePromise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });

    return firestorePromise;
  }

  /**
   * Check Firestore cache, fallback to TMDB queue if not found
   * Includes a timeout as a safety net in case Firestore is slow/unresponsive
   */
  private async checkFirestoreCacheWithSource(
    movieId: string,
    cacheKey: string
  ): Promise<{ data: any; source: TmdbMovieDataSource }> {
    try {
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          console.warn(`⏰ Firestore cache check timed out for ${movieId} after 10s`);
          resolve(null);
        }, 10000);
      });

      const firestoreCached = await Promise.race([
        movieCacheService.getMovieData(movieId),
        timeoutPromise
      ]);

      if (firestoreCached) {
        console.log(`🔥 Firestore cache hit for movie ${movieId}`);

        const movieData = this.convertFirestoreToTMDBFormat(firestoreCached);

        if (isRealTmdbPayloadForQueueCache(movieData)) {
          this.setCache(cacheKey, movieData);
        }

        return { data: movieData, source: 'firestore' };
      }
    } catch (error) {
      console.warn(`⚠️ Firestore cache check failed for ${movieId}, falling back to TMDB:`, error);
    }

    return this.queueTMDBRequest(movieId, cacheKey);
  }

  /**
   * Convert Firestore cache format to TMDB API format
   */
  private convertFirestoreToTMDBFormat(cached: any): any {
    return {
      id: cached.id,
      imdbID: cached.imdbId || `tmdb_${cached.id}`,
      title: cached.title,
      Title: cached.title,
      year: cached.year,
      Year: cached.year,
      release_date: cached.releaseDate,
      poster_path: cached.posterPath,
      Poster: cached.posterPath ? `https://image.tmdb.org/t/p/w500${cached.posterPath}` : null,
      backdrop_path: cached.backdropPath,
      overview: cached.overview,
      Plot: cached.overview,
      runtime: cached.runtime,
      Runtime: cached.runtime ? `${cached.runtime} min` : 'N/A',
      vote_average: cached.voteAverage,
      imdbRating: cached.voteAverage?.toFixed(1) || 'N/A',
      genres: cached.genres,
      // Extract genre_ids from genres array for filter compatibility
      genre_ids: cached.genres?.map((g: any) => g.id) || cached.genreIds || [],
      Genre: cached.genres?.map((g: any) => g.name).join(', ') || '',
      // Add original_language for language filter
      original_language: cached.originalLanguage || cached.original_language || '',
      credits: {
        crew: cached.directors?.map((d: any) => ({
          id: d.id,
          name: d.name,
          profile_path: d.profilePath,
          job: 'Director',
        })) || [],
        cast: cached.mainCast?.map((a: any) => ({
          id: a.id,
          name: a.name,
          character: a.character,
          profile_path: a.profilePath,
          order: a.order,
        })) || [],
      },
      Response: 'True',
    };
  }

  /**
   * Queue a TMDB API request (original queue logic)
   */
  private queueTMDBRequest(movieId: string, cacheKey: string): Promise<{ data: any; source: 'network' }> {
    return new Promise<{ data: any; source: 'network' }>((resolve) => {
      const requestId = `${cacheKey}_${Date.now()}`;
      const queueItem: QueuedRequest = {
        id: requestId,
        movieId,
        resolve,
        reject: () => {
          /* legacy; errors resolve with fallback payload instead */
        },
        timestamp: Date.now(),
        retryCount: 0
      };

      this.queue.push(queueItem);
      console.log(`📋 Queued movie request ${movieId} (queue size: ${this.queue.length})`);

      // Start processing if not already running
      if (!this.isProcessing) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the queue with rate limiting
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    console.log(`🚀 Starting queue processing (${this.queue.length} items)`);

    while (this.queue.length > 0 && this.activeRequests < this.MAX_CONCURRENT) {
      const batch = this.queue.splice(0, this.MAX_CONCURRENT - this.activeRequests);

      // Process batch with rate limiting
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;

      if (timeSinceLastRequest < this.RATE_LIMIT_DELAY) {
        const delay = this.RATE_LIMIT_DELAY - timeSinceLastRequest;
        console.log(`⏱️ Rate limiting: waiting ${delay}ms before next batch`);
        await this.sleep(delay);
      }

      // Process batch concurrently
      batch.forEach(request => this.processRequest(request));
      this.lastRequestTime = Date.now();

      // Small delay between batches to prevent overwhelming the API
      if (this.queue.length > 0) {
        await this.sleep(200); // 200ms between batches
      }
    }

    // Allow subsequent processing cycles or mark queue as idle
    this.isProcessing = false;

    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 100);
    } else {
      this.markQueueIdle();
    }
  }

  /**
   * Process individual request with retry logic
   */
  private async processRequest(request: QueuedRequest): Promise<void> {
    this.activeRequests++;
    const cacheKey = this.getCacheKey(request.movieId);

    try {
      console.log(`🎬 Fetching movie data for ${request.movieId} (attempt ${request.retryCount + 1})`);

      // Import tmdbService dynamically to avoid circular dependency
      const { tmdbService } = await import('./tmdbApi');

      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
      });

      const movieData = await Promise.race([
        tmdbService.getMovieById(request.movieId),
        timeoutPromise
      ]) as any;

      if (movieData.Response === "True" || movieData.title) {
        if (isRealTmdbPayloadForQueueCache(movieData)) {
          this.setCache(cacheKey, movieData);
        } else {
          console.warn(
            `⚠️ Skipping cache for ${request.movieId}: payload looks like TMDB fallback (missing real id)`
          );
        }
        request.resolve({ data: movieData, source: 'network' });
        console.log(`✅ Successfully fetched movie ${request.movieId}`);
      } else {
        throw new Error(`Movie not found: ${request.movieId}`);
      }
    } catch (error: any) {
      console.warn(`❌ Error fetching movie ${request.movieId}:`, error.message);

      // Check if it's a rate limit, timeout, or network error
      const is429Error = error.message?.includes('429') || error.message?.includes('Maximum');
      const isRetryableError = is429Error ||
        error.message?.includes('rate limit') ||
        error.message?.includes('timeout') ||
        error.message?.includes('network') ||
        error.message?.includes('Request timeout');

      if (isRetryableError && request.retryCount < this.MAX_RETRIES) {
        // For 429 errors, use longer delays
        const baseDelay = is429Error ? 10000 : (this.RETRY_DELAYS[request.retryCount] || 5000);
        const retryDelay = baseDelay * (request.retryCount + 1); // Progressive backoff
        request.retryCount++;

        console.log(`🔄 Retrying movie ${request.movieId} in ${retryDelay}ms (attempt ${request.retryCount + 1})`);

        setTimeout(() => {
          this.queue.unshift(request); // Add back to front of queue
          if (!this.isProcessing) {
            this.processQueue();
          }
        }, retryDelay);
      } else {
        // Max retries exceeded or non-retryable error
        console.error(`🔥 ${isRetryableError ? 'Max retries exceeded' : 'Non-retryable error'} for movie ${request.movieId}`);

        // Return a minimal fallback response instead of rejecting
        const fallbackData = {
          Response: "False",
          Error: "Movie not found",
          imdbID: request.movieId,
          Title: formatMovieDisplayTitleFromId(request.movieId),
        };

        request.resolve({ data: fallbackData, source: 'network' });
      }
    } finally {
      this.activeRequests--;

      if (this.queue.length > 0) {
        if (!this.isProcessing) {
          setTimeout(() => this.processQueue(), 0);
        }
      } else {
        this.markQueueIdle();
      }
    }
  }

  /**
   * Reset processing flag when queue and active requests are clear
   */
  private markQueueIdle(): void {
    if (this.queue.length === 0 && this.activeRequests === 0) {
      this.isProcessing = false;
      console.log(`✅ Queue processing completed`);
    }
  }

  /**
   * Cache management
   */
  private getCacheKey(movieId: string): string {
    return `movie_${movieId}`;
  }

  private getFromCache(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    if (!isRealTmdbPayloadForQueueCache(cached.data)) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  private setCache(key: string, data: any): void {
    if (!isRealTmdbPayloadForQueueCache(data)) {
      return;
    }
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL
    });

    // Clean up old cache entries periodically
    if (this.cache.size > 500) {
      this.cleanupCache();
    }
  }

  /**
   * Load cache from localStorage on initialization
   */
  private loadCacheFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsedCache = JSON.parse(stored);
        const now = Date.now();

        // Filter out expired entries
        Object.entries(parsedCache).forEach(([key, cached]: [string, any]) => {
          if (cached && typeof cached === 'object' && cached.timestamp && cached.data) {
            if (now - cached.timestamp < this.CACHE_TTL && isRealTmdbPayloadForQueueCache(cached.data)) {
              this.cache.set(key, cached);
            }
          }
        });

        console.log(`📦 Loaded ${this.cache.size} cached movies from storage`);
      }
    } catch (error) {
      console.warn('Failed to load TMDB cache from storage:', error);
      // Clear corrupted cache
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveCacheToStorage(): void {
    try {
      // Convert Map to object for JSON serialization
      const cacheObject: { [key: string]: CachedMovie } = {};
      const now = Date.now();

      // Only save non-expired entries
      this.cache.forEach((cached, key) => {
        if (now - cached.timestamp < this.CACHE_TTL) {
          cacheObject[key] = cached;
        }
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheObject));
      console.log(`💾 Saved ${Object.keys(cacheObject).length} cached movies to storage`);
    } catch (error) {
      console.warn('Failed to save TMDB cache to storage:', error);
    }
  }

  private cleanupCache(): void {
    const now = Date.now();
    let cleaned = 0;

    // Convert iterator to array to avoid TypeScript iteration issues
    const entries = Array.from(this.cache.entries());
    for (const [key, cached] of entries) {
      if (now - cached.timestamp > cached.ttl) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    console.log(`🧹 Cleaned up ${cleaned} expired cache entries`);

    // Save updated cache to storage after cleanup
    if (cleaned > 0) {
      this.saveCacheToStorage();
    }
  }

  /**
   * Utility function for delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get queue status for debugging
   */
  getStatus() {
    return {
      queueSize: this.queue.length,
      activeRequests: this.activeRequests,
      cacheSize: this.cache.size,
      isProcessing: this.isProcessing,
      pendingRequests: this.pendingRequests.size
    };
  }

  /**
   * Clear cache (useful for testing or when cache format changes)
   */
  clearCache(): void {
    this.cache.clear();
    localStorage.removeItem(this.STORAGE_KEY);
    // Keep version so cache isn't immediately invalidated again
    localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION.toString());
    console.log('🧹 TMDB cache cleared from memory and storage');
  }

  /**
   * Seed local TMDB queue cache (e.g. after Firebase batchGetMovieDetails).
   */
  seedCachedMovie(movieId: string, data: any): void {
    if (!data || !isRealTmdbPayloadForQueueCache(data)) return;
    this.setCache(this.getCacheKey(movieId), data);
  }

  private async prefetchUncachedViaFunctionsBatch(uncachedUniqueIds: string[]): Promise<void> {
    if (uncachedUniqueIds.length === 0) return;
    if (!shouldUseFunctionsAPI()) return;

    let hasUser = false;
    try {
      const { getAuth } = await import('./firebase');
      const auth = await getAuth();
      hasUser = !!auth.currentUser;
    } catch {
      return;
    }
    if (!hasUser) return;

    const { chunkMovieIds, normalizeBatchMovieForQueueCache } = await import('./batchMovieHydration');
    const { prefetchMovieDetails } = await import('./batchedMovieService');
    const chunks = chunkMovieIds(uncachedUniqueIds, 50);

    for (const chunk of chunks) {
      try {
        const map = await prefetchMovieDetails(chunk);
        map.forEach((movie, id) => {
          const normalized = normalizeBatchMovieForQueueCache(movie as unknown as Record<string, unknown>);
          if (normalized) {
            this.seedCachedMovie(id, normalized);
          }
        });
      } catch (e) {
        console.warn('tmdbRequestQueue: batchGetMovieDetails prefetch failed, using per-movie fallback', e);
      }
    }
  }

  /**
   * Batch request multiple movies efficiently.
   * Order matches input `movieIds` (including duplicates). Uses Firebase batchGetMovieDetails when signed in + Functions API enabled, then per-movie queue fallback.
   */
  async getMoviesBatch(movieIds: string[]): Promise<(any | null)[]> {
    console.log(`📦 Batch requesting ${movieIds.length} movies`);

    const uniqueIds = Array.from(new Set(movieIds));
    const idToMovie = new Map<string, any | null>();

    for (const movieId of uniqueIds) {
      const cached = this.getFromCache(this.getCacheKey(movieId));
      idToMovie.set(movieId, cached ?? null);
    }

    let uncachedUnique = uniqueIds.filter(id => idToMovie.get(id) == null);

    if (uncachedUnique.length > 0) {
      await this.prefetchUncachedViaFunctionsBatch(uncachedUnique);
      for (const movieId of uncachedUnique) {
        const cached = this.getFromCache(this.getCacheKey(movieId));
        if (cached) {
          idToMovie.set(movieId, cached);
        }
      }
    }

    uncachedUnique = uniqueIds.filter(id => idToMovie.get(id) == null);

    if (uncachedUnique.length > 0) {
      console.log(`🎬 Fetching ${uncachedUnique.length} uncached movies from batch (per-movie path)`);

      const fetchPromises = uncachedUnique.map(movieId =>
        this.getMovieData(movieId).catch(error => {
          console.warn(`Failed to fetch movie ${movieId} in batch:`, error);
          return null;
        })
      );

      const fetchedResults = await Promise.allSettled(fetchPromises);

      uncachedUnique.forEach((movieId, idx) => {
        const result = fetchedResults[idx];
        idToMovie.set(movieId, result.status === 'fulfilled' ? result.value : null);
      });
    }

    const out = movieIds.map(id => idToMovie.get(id) ?? null);
    console.log(`✅ Batch completed: ${out.filter(r => r !== null).length}/${movieIds.length} successful`);
    return out;
  }
}

// Export singleton instance
export const tmdbRequestQueue = new TMDbRequestQueue();
export default tmdbRequestQueue; 
